/**
 * A route target as configured in a dynamic field: a page or workflow id plus
 * values for its route parameters. Legacy values can contain only the target id.
 */
export type RouteTargetConfig =
  | string
  | {
      id: string;
      params?: Record<string, unknown>;
    };

/**
 * Extract the target ID and parameters from a route target configuration.
 */
export function extractRouteTarget(config: RouteTargetConfig | null): {
  targetId: string | null;
  targetParams: Record<string, unknown>;
} {
  if (typeof config === 'string') {
    return {
      targetId: config || null,
      targetParams: {}
    };
  }

  return {
    targetId: config?.id ?? null,
    targetParams: config?.params ?? {}
  };
}

/**
 * Builds a route URL by substituting route parameters with provided values.
 */
export function buildRouteUrl(
  route: string,
  params: Record<string, unknown> | null | undefined
): string {
  if (!params || Object.keys(params).length === 0) {
    return route;
  }

  return route.replace(/\{([^}]+)\}/g, (match, paramName) => {
    const value = params[paramName];
    if (value === undefined || value === null || value === '') {
      // Keep placeholder if value is not provided
      return match;
    }
    return encodeURIComponent(value as string);
  });
}

/**
 * Extracts parameter names from a route pattern
 *
 * @param route - The route pattern (e.g., "/todos/{id}/edit")
 * @returns Array of parameter names
 *
 * @example
 * extractRouteParams('/todos/{id}/comments/{commentId}')
 * // Returns: ['id', 'commentId']
 */
export function extractRouteParams(route: string | null | undefined): string[] {
  if (!route) {
    return [];
  }

  const matches = route.match(/\{([^}]+)\}/g);
  if (!matches) {
    return [];
  }

  return matches.map((match) => match.slice(1, -1));
}

/**
 * Matches a URL path against a route pattern and reads the param values out of it.
 *
 * Unlike {@link extractRouteParamValues} a param-less route that matches resolves
 * to an empty record rather than null, so callers can tell "this path is that
 * route, with no params" apart from "this path is not that route".
 *
 * @example
 * matchRoutePath("colleges/{id}", "/colleges/abc-123") // { id: "abc-123" }
 * matchRoutePath("/about", "/about") // {}
 * matchRoutePath("/about", "/contact") // null
 */
export function matchRoutePath(
  routePattern: string | null | undefined,
  currentPath: string | null | undefined
): Record<string, string> | null {
  if (!routePattern || !currentPath) {
    return null;
  }

  const normalizedPattern = routePattern.startsWith('/') ? routePattern : `/${routePattern}`;
  const normalizedPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;

  const paramNames = extractRouteParams(normalizedPattern);
  const regexPattern = normalizedPattern.replace(/\{([^}]+)\}/g, '([^/]+)');
  const match = normalizedPath.match(new RegExp(`^${regexPattern}$`));

  if (!match) {
    return null;
  }

  const result: Record<string, string> = {};
  paramNames.forEach((name, index) => {
    result[name] = decodeURIComponent(match[index + 1]);
  });

  return result;
}

/**
 * Rewrite historical route param keys onto the keys a route currently uses, so a
 * value addressed by an older name still fills today's placeholder. A param
 * already named by a current key wins over the same param named by an old one,
 * and a key the trail says nothing about is passed through untouched.
 *
 * This is steps 1 and 2 of the resolution order documented on
 * {@link import('@Core/lib/tasks/utils/routeUrl').interpolateRouteParams}. The
 * guarded positional fallback is deliberately left out: link building may guess
 * at a rename that predates rename tracking, but a preview reading these keys
 * would overwrite values the author entered by hand, so it only acts on renames
 * the trail can prove.
 *
 * @param routeParamAliases - old key => current key
 */
export function withCurrentRouteParamKeys(
  routeParams: Record<string, string>,
  routeParamNames: string[],
  routeParamAliases: Record<string, string>
): Record<string, string> {
  const currentKeys = new Set(routeParamNames);
  const entries = Object.entries(routeParams);
  const renamed: Record<string, string> = {};

  for (const [key, value] of entries) {
    if (routeParamAliases[key] === undefined || currentKeys.has(key)) {
      renamed[key] = value;
    }
  }

  for (const [key, value] of entries) {
    const currentKey = routeParamAliases[key];

    if (currentKey === undefined || currentKeys.has(key) || currentKey in renamed) {
      continue;
    }

    renamed[currentKey] = value;
  }

  return renamed;
}

/**
 * Extracts parameter values from a URL path by matching it against a route pattern.
 *
 * @param routePattern - The route pattern (e.g., "colleges/{id}")
 * @param currentPath - The current URL path (e.g., "/colleges/019d02ba-83ed-73b4-b4a0-fa76ad7374e8")
 * @returns Record of parameter names to their values, or null if the path doesn't match
 *
 * @example
 * extractRouteParamValues("colleges/{id}", "/colleges/abc-123")
 * // Returns: { id: "abc-123" }
 */
export function extractRouteParamValues(
  routePattern: string | null | undefined,
  currentPath: string | null | undefined
): Record<string, string> | null {
  if (extractRouteParams(routePattern).length === 0) {
    return null;
  }

  return matchRoutePath(routePattern, currentPath);
}
